<template>
  <div class="inventory-layout">
    <!-- 主内容区 -->
    <main class="inventory-content">
      <!-- 搜索和筛选区域 -->
      <div class="filter-section">
        <div class="filter-header">
          <h1 class="page-title">库存管理</h1>
        </div>
        <div class="filter-controls">
          <div class="search-bar">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索商品名称或条码..."
              class="search-input"
              @keyup.enter="handleSearch"
            />
            <button class="search-button" @click="handleSearch">搜索</button>
            <button
              class="refresh-inventory-btn"
              @click="refreshInventory"
              :disabled="isLoading"
              title="刷新库存列表"
            >
              <img
                src="/img/refresh.png"
                alt="刷新"
                class="refresh-icon"
                :class="{ 'spinning': isLoading }"
              >
            </button>
          </div>
          <div class="category-group" v-if="categories.length > 0">
            <div class="filter-label">分类：</div>
            <div class="category-filters">
              <div
                v-for="category in categories"
                :key="category.id"
                :class="['category-filter', { active: selectedCategory === category.id }]"
                @click="selectCategory(category.id)"
              >
                {{ category.name }}
              </div>
            </div>
          </div>
          <button class="reset-button" @click="resetFilters">重置</button>
        </div>
      </div>

      <!-- 库存统计 -->
      <div class="inventory-stats">
        <div class="stat-card">
          <div class="stat-label">商品种类</div>
          <div class="stat-value">{{ totalProducts }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">库存总量</div>
          <div class="stat-value">{{ totalStock }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">低库存预警</div>
          <div class="stat-value warning">{{ lowStockCount }}</div>
        </div>
      </div>

      <!-- 库存表格 -->
      <div class="inventory-table-container">
        <table class="inventory-table">
          <thead>
            <tr>
              <th>商品信息</th>
              <th>商品条码</th>
              <th>分类</th>
              <th class="sortable-header" @click="toggleStockSort">
                当前库存
                <span class="sort-indicator">
                  <span v-if="stockSortOrder === 'asc'" class="sort-asc">↑</span>
                  <span v-else-if="stockSortOrder === 'desc'" class="sort-desc">↓</span>
                  <span v-else class="sort-none">↕</span>
                </span>
              </th>
              <th>赠品库存</th>
              <th>单价</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedProducts.length === 0">
              <td colspan="6" class="no-data">暂无库存数据</td>
            </tr>
            <tr v-for="product in paginatedProducts" :key="product.id || Math.random()">
              <td class="product-info">
                <div class="product-name">{{ product.name || '未知商品' }}</div>
              </td>
              <td class="product-code">{{ product.code || 'N/A' }}</td>
              <td class="product-category">
                <span class="category-tag">{{ product.category_name || '未分类' }}</span>
              </td>
              <td class="stock-cell">
                <span :class="['stock-value', { 'low-stock': (product.stock || 0) <= 10 }]">
                  {{ product.stock || 0 }}
                </span>
              </td>
              <td class="gift-stock-cell">
                <span class="gift-stock-value">{{ product.gift_stock || 0 }}</span>
              </td>
              <td class="price-cell">¥{{ (product.price || 0).toFixed(2) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="pagination">
        <div class="pagination-info">
          显示 {{ products.length > 0 ? (currentPage - 1) * pageSize + 1 : 0 }}
          - {{ Math.min(currentPage * pageSize, products.length) }}
          共 {{ stockStats?.total || products.length || 0 }} 条
        </div>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="currentPage === 1 || totalPages <= 1"
            @click="currentPage--"
          >
            上一页
          </button>
          <span class="page-number">{{ currentPage }} / {{ Math.max(totalPages, 1) }}</span>
          <button
            class="page-btn"
            :disabled="currentPage >= totalPages || totalPages <= 1"
            @click="currentPage++"
          >
            下一页
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { cashierApi } from '../api/cashier.js'

export default {
  name: 'InventoryView',
  props: {
    userInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      searchKeyword: '',
      selectedCategory: 0, // 改为0表示全部，与Home.vue保持一致
      currentPage: 1,
      stockSortOrder: null, // null, 'asc', 'desc'
      pageSize: 15,
      categories: [], // 从API加载分类
      products: [], // 从API加载商品
      stockStats: null, // 库存统计信息
      isLoading: false // 是否正在加载库存数据
    }
  },
  computed: {
    // 直接返回products，因为筛选和分页在服务端完成
    paginatedProducts() {
      return this.products || []
    },
    totalPages() {
      const total = this.stockStats?.total || this.products?.length || 0
      return Math.max(Math.ceil(total / this.pageSize), 1)
    },
    totalStock() {
      // 使用服务端统计的库存总量，如果没有则从当前页面计算
      return (this.products || []).reduce((total, product) => total + (product.stock || 0), 0)
    },
    lowStockCount() {
      // 使用服务端统计的低库存数量，如果没有则从当前页面计算
      if (this.stockStats?.low_stock_count !== undefined) {
        return this.stockStats.low_stock_count
      }
      return (this.products || []).filter(product => (product.stock || 0) <= 10).length
    },
    totalProducts() {
      return this.stockStats?.total || (this.products || []).length
    }
  },
  methods: {
    toggleStockSort() {
      if (this.stockSortOrder === null) {
        this.stockSortOrder = 'asc'
      } else if (this.stockSortOrder === 'asc') {
        this.stockSortOrder = 'desc'
      } else {
        this.stockSortOrder = null
      }
      this.currentPage = 1 // 重置到第一页
      this.loadProducts() // 重新加载数据
    },
    selectCategory(categoryId) {
      this.selectedCategory = categoryId
      this.currentPage = 1
      this.loadProducts() // 重新加载数据
    },
    handleSearch() {
      if (!this.searchKeyword.trim()) {
        alert('请输入搜索关键词')
        return
      }
      this.currentPage = 1
      this.loadProducts() // 重新加载数据
    },
    resetFilters() {
      this.searchKeyword = ''
      this.selectedCategory = 0 // 改为0，表示全部
      this.stockSortOrder = null
      this.currentPage = 1
      this.loadProducts() // 重新加载数据
    },
    getCategoryName(categoryId) {
      const category = this.categories.find(c => c.id === categoryId)
      return category ? category.name : '未分类'
    },

    // 加载商品分类
    async loadCategories() {
      try {
        const result = await cashierApi.getShopCategories()
        if (result.success && result.data) {
          // 添加"全部"分类到最前面
          this.categories = [
            { id: 0, name: '全部', text: '全' },
            ...result.data.map(cat => ({
              id: cat.id,
              name: cat.title,
              text: cat.title.charAt(0)
            }))
          ]
          console.log('商品分类加载成功:', this.categories)
        } else {
          console.error('获取商品分类失败:', result.message)
        }
      } catch (error) {
        console.error('获取商品分类异常:', error)
      }
    },

    // 加载库存商品列表
    async loadProducts() {
      try {
        this.isLoading = true
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize
        }

        // 只在有值时才添加参数
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword
        }
        if (this.selectedCategory !== 0) {
          params.categoryId = this.selectedCategory
        }
        if (this.stockSortOrder) {
          params.stockType = this.stockSortOrder === 'asc' ? 'low' : 'normal'
        }

        const result = await cashierApi.getInventoryStock(params)
        if (result.success && result.data) {
          this.products = result.data.list.map(item => {
            const product = item.product
            return {
              id: product.ID,
              name: product.name,
              price: product.normal_price || 0,
              member_price: product.member_price || 0,
              stock: item.quantity,
              gift_stock: item.gift_quantity || 0,
              category_id: product.category ? product.category.id : 0,
              category_name: product.category ? product.category.title : '',
              is_gift: product.is_gift || false,
              status: product.status || '1',
              code: product.code || product.ID.toString(), // 优先使用商品编码
              text: product.name ? product.name.charAt(0) : '',
              sync_time: item.sync_time,
              sync_by: item.sync_by
            }
          })

          // 更新统计信息
          if (result.data.stats) {
            this.stockStats = result.data.stats
          }

          console.log('库存商品列表加载成功:', this.products)
        } else {
          console.error('获取库存商品列表失败:', result.message)
        }
      } catch (error) {
        console.error('获取库存商品列表异常:', error)
      } finally {
        this.isLoading = false
      }
    },

    // 刷新库存列表
    async refreshInventory() {
      console.log('刷新库存列表...')
      try {
        // 重置到第一页并重新加载分类和库存数据
        this.currentPage = 1
        await Promise.all([
          this.loadCategories(),
          this.loadProducts()
        ])
        alert('库存列表已刷新')
      } catch (error) {
        console.error('刷新库存列表失败:', error)
        alert('刷新库存列表失败')
      }
    }
  },
  watch: {
    currentPage() {
      this.loadProducts()
    }
  },
  mounted() {
    // 先加载分类，再加载商品
    this.loadCategories().then(() => {
      this.loadProducts()
    })
  }
}
</script>

<style scoped>
.inventory-layout {
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Microsoft YaHei', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 主内容区 */
.inventory-content {
  flex: 1;
  height: 100vh;
  padding: 24px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-sizing: border-box;
  min-width: 0;
}

.page-title {
  margin: 0 0 20px 0;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.5px;
  position: relative;
  padding-left: 20px;
}

.page-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 28px;
  background: linear-gradient(180deg, #667eea, #764ba2);
  border-radius: 2px;
}

/* 筛选区域样式 */
.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 450px;
}

.search-input {
  border: 2px solid #e1e5e9;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  font-size: 14px;
  width: 100%;
  outline: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #f8fafc;
  color: #334155;
  font-weight: 500;
}

.search-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  background: white;
}

.search-input::placeholder {
  color: #94a3b8;
}

.search-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 80px;
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.search-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.refresh-inventory-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #52c41a, #389e0d);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(82, 196, 26, 0.3);
  padding: 0;
  flex-shrink: 0;
}

.refresh-inventory-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(82, 196, 26, 0.4);
}

.refresh-inventory-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.refresh-inventory-btn .refresh-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.9;
  transition: all 0.3s ease;
}

.refresh-inventory-btn:hover:not(:disabled) .refresh-icon {
  opacity: 1;
}

.refresh-inventory-btn .refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

.reset-button {
  background: #f1f5f9;
  color: #64748b;
  border: 2px solid #e2e8f0;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 80px;
  white-space: nowrap;
}

.reset-button:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
  color: #475569;
  transform: translateY(-1px);
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 24px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.filter-header {
  margin-bottom: 20px;
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.category-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-label {
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  font-size: 14px;
  letter-spacing: 0.2px;
}

.category-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.category-filter {
  padding: 0 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  font-size: 13px;
  background: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  min-width: 70px;
  text-align: center;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #6b7280;
}

.category-filter:hover {
  border-color: #667eea;
  color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.category-filter.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

/* 统计卡片 */
.inventory-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 28px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.stat-value {
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-value.warning {
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 表格 */
.inventory-table-container {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.4);
  margin-bottom: 24px;
}

.inventory-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.inventory-table th {
  background: #f8fafc;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e2e8f0;
  font-size: 13px;
  white-space: nowrap;
  position: relative;
}

.inventory-table th:nth-child(1) {
  width: 30%;
}

.inventory-table th:nth-child(2) {
  width: 20%;
}

.inventory-table th:nth-child(3) {
  width: 15%;
}

.inventory-table th:nth-child(4) {
  width: 12%;
}

.inventory-table th:nth-child(5) {
  width: 12%;
}

.inventory-table th:nth-child(6) {
  width: 18%;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  position: relative;
  transition: all 0.3s ease;
}

.sortable-header:hover {
  background: #e2e8f0;
}

.sort-indicator {
  margin-left: 8px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.sort-asc {
  color: #10b981;
}

.sort-desc {
  color: #ef4444;
}

.sort-none {
  color: #94a3b8;
}

.inventory-table td {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  font-size: 14px;
  color: #374151;
}

.inventory-table tbody tr {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.inventory-table tbody tr:hover {
  background: rgba(102, 126, 234, 0.05);
  transform: scale(1.005);
}

.inventory-table tbody tr:hover::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, #667eea, #764ba2);
}

.product-info {
  min-width: 150px;
}

.product-name {
  font-weight: 500;
  color: #333;
  line-height: 1.4;
}

.product-code {
  color: #999;
  font-size: 12px;
  font-family: monospace;
}

.category-tag {
  background-color: #e6f7ff;
  color: #1890ff;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.stock-cell {
  text-align: center;
  font-weight: 600;
}

.stock-value.low-stock {
  color: #f5222d;
}

.gift-stock-cell {
  text-align: center;
  font-weight: 600;
}

.gift-stock-value {
  color: #52c41a;
  background-color: #f6ffed;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 13px;
}

.price-cell {
  font-weight: 600;
  color: #e74c3c;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 40px;
  font-size: 14px;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 24px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-sizing: border-box;
}

.pagination-info {
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-btn {
  background: white;
  border: 2px solid #e5e7eb;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.page-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.page-number {
  font-weight: 700;
  color: #1e293b;
  font-size: 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  padding: 8px 16px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 12px;
  min-width: 60px;
  text-align: center;
}
</style>